import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, CalendarDays, Loader2, Phone, Home, FileText, Bell, List, CalendarRange, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { AgendaCalendarView, brtDateKey } from "./AgendaCalendar";

const CATEGORIES = {
  followup: { label: "Follow-up", icon: Phone, cls: "bg-blue-100 text-blue-700 border-blue-200" },
  visita: { label: "Visita", icon: Home, cls: "bg-amber-100 text-amber-700 border-amber-200" },
  documento: { label: "Documentação", icon: FileText, cls: "bg-purple-100 text-purple-700 border-purple-200" },
  geral: { label: "Geral", icon: Bell, cls: "bg-stone-100 text-stone-600 border-stone-200" },
};

const EMPTY_FORM = { title: "", category: "auto", date: "", time: "09:00", notes: "", lead_name: "", kind: "compromisso" };

const fmtBRT = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
  } catch { return ""; }
};

function TaskRow({ task, onToggle, onEdit, onDelete }) {
  const cat = CATEGORIES[task.category] || CATEGORIES.geral;
  const Icon = cat.icon;
  const overdue = !task.done && new Date(task.remind_at) < new Date();
  return (
    <div data-testid={`task-row-${task.id}`}
      className={`flex items-start gap-3 p-3 rounded-lg border bg-white ${task.done ? "opacity-60" : ""}`}>
      <Checkbox data-testid={`task-done-${task.id}`} checked={!!task.done}
        onCheckedChange={(v) => onToggle(task, !!v)} className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium text-[#1B3A2F] ${task.done ? "line-through" : ""}`}>{task.title}</div>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          <Badge className={`text-[11px] font-normal border gap-1 ${cat.cls}`}>
            <Icon className="w-3 h-3" /> {cat.label}
          </Badge>
          <Badge variant="outline" className="text-[11px] font-normal" data-testid={`task-kind-${task.id}`}>
            {(task.kind || "compromisso") === "compromisso" ? "Agenda" : "Tarefa"}
          </Badge>
          <span className={`text-xs ${overdue ? "text-red-600 font-semibold" : "text-stone-400"}`}>
            {fmtBRT(task.remind_at)}{overdue ? " · atrasada" : ""}
          </span>
          {task.lead_name && <Badge variant="outline" className="text-[11px] font-normal">{task.lead_name}</Badge>}
          {task.source === "ai" && <span className="text-[10px] text-stone-300 uppercase tracking-wide">via WhatsApp</span>}
        </div>
        {task.notes && <div className="text-xs text-stone-400 mt-1 truncate">{task.notes}</div>}
      </div>
      <button data-testid={`task-edit-${task.id}`} onClick={() => onEdit(task)}
        className="p-1 text-stone-400 hover:text-stone-700"><Pencil className="w-4 h-4" /></button>
      <button data-testid={`task-delete-${task.id}`} onClick={() => onDelete(task)}
        className="p-1 text-stone-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

function Section({ title, tasks, emptyText, ...handlers }) {
  return (
    <div>
      <h2 className="text-sm font-bold text-[#1B3A2F] uppercase tracking-wide mb-2">{title} <span className="text-stone-400 font-normal">({tasks.length})</span></h2>
      {tasks.length === 0 ? (
        <div className="text-xs text-stone-400 border border-dashed rounded-lg p-3">{emptyText}</div>
      ) : (
        <div className="space-y-2">{tasks.map((t) => <TaskRow key={t.id} task={t} {...handlers} />)}</div>
      )}
    </div>
  );
}

export default function Agenda() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("lista");
  const [selectedDay, setSelectedDay] = useState(null);

  const [agendaToday, setAgendaToday] = useState(null);

  const load = async () => {
    try {
      const [res, today] = await Promise.all([api.get("/tasks"), api.get("/agenda/today")]);
      setTasks(res.data);
      setAgendaToday(today.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    const today = new Date();
    setForm({ ...EMPTY_FORM, category: "auto", date: today.toISOString().slice(0, 10) });
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (task) => {
    const d = new Date(task.remind_at);
    const brt = new Date(d.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
    setForm({
      title: task.title, category: task.category || "geral",
      kind: task.kind || "compromisso",
      date: `${brt.getFullYear()}-${String(brt.getMonth() + 1).padStart(2, "0")}-${String(brt.getDate()).padStart(2, "0")}`,
      time: `${String(brt.getHours()).padStart(2, "0")}:${String(brt.getMinutes()).padStart(2, "0")}`,
      notes: task.notes || "", lead_name: task.lead_name || "",
    });
    setEditingId(task.id);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.date || !form.time) {
      toast.error("Preenche título, data e hora");
      return;
    }
    setSaving(true);
    try {
      // horário digitado é BRT (UTC-3) → converte pra UTC
      const [h, m] = form.time.split(":").map(Number);
      const dt = new Date(`${form.date}T${form.time}:00-03:00`);
      const payload = {
        title: form.title.trim(), kind: form.kind,
        has_time: form.kind === "compromisso",
        remind_at: dt.toISOString(), notes: form.notes, lead_name: form.lead_name,
      };
      if (form.category && form.category !== "auto") payload.category = form.category;
      if (editingId) {
        await api.patch(`/tasks/${editingId}`, payload);
        toast.success("Tarefa atualizada");
      } else {
        await api.post("/tasks", payload);
        toast.success("Tarefa criada. Te lembro no WhatsApp na hora certa!");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar tarefa");
    } finally { setSaving(false); }
  };

  const toggleDone = async (task, done) => {
    setTasks((ts) => ts.map((t) => (t.id === task.id ? { ...t, done } : t)));
    try {
      await api.patch(`/tasks/${task.id}`, { done });
    } catch { load(); }
  };

  const remove = async (task) => {
    setTasks((ts) => ts.filter((t) => t.id !== task.id));
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Tarefa excluída");
    } catch { load(); }
  };

  const filtered = useMemo(
    () => (filter === "all" ? tasks : tasks.filter((t) => (t.category || "geral") === filter)),
    [tasks, filter]
  );

  const now = new Date();
  const pending = filtered.filter((t) => !t.done);
  const overdue = pending.filter((t) => new Date(t.remind_at) < now);
  const catOf = (t) => (t.category || "geral");
  const matchesFilter = (t) => filter === "all" || catOf(t) === filter;
  const compromissos = (agendaToday?.compromissos || []).filter((t) => t.today && !t.overdue && matchesFilter(t));
  const tarefas = (agendaToday?.tarefas || []).filter((t) => t.today && !t.overdue && matchesFilter(t));
  const dayIds = new Set([...compromissos, ...tarefas, ...overdue].map((t) => t.id));
  const upcoming = pending.filter((t) => !dayIds.has(t.id) && new Date(t.remind_at) > now);
  const doneTasks = filtered.filter((t) => t.done).slice(-15).reverse();

  const handlers = { onToggle: toggleDone, onEdit: openEdit, onDelete: remove };

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto space-y-6" data-testid="agenda-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4651A] font-bold mb-1">Organização</div>
          <h1 className="font-heading text-3xl font-bold text-[#1B3A2F] flex items-center gap-2">
            <CalendarDays className="w-7 h-7" /> Agenda
          </h1>
          <p className="text-stone-500 text-sm mt-1">Agenda são compromissos com hora marcada. Tarefas são o que precisa sair hoje, sem hora fixa. A IA te cobra no WhatsApp na hora certa.</p>
        </div>
        <Button onClick={openNew} data-testid="agenda-new-task-btn" className="bg-[#D4651A] hover:bg-[#B85512] text-white gap-2">
          <Plus className="w-4 h-4" /> Nova tarefa
        </Button>
      </header>

      <div className="flex gap-2 flex-wrap" data-testid="agenda-filters">
        <button onClick={() => setFilter("all")} data-testid="agenda-filter-all"
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === "all" ? "bg-[#1B3A2F] text-white border-[#1B3A2F]" : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"}`}>
          Todas ({tasks.length})
        </button>
        {Object.entries(CATEGORIES).map(([key, c]) => {
          const count = tasks.filter((t) => (t.category || "geral") === key).length;
          return (
            <button key={key} onClick={() => setFilter(key)} data-testid={`agenda-filter-${key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === key ? "bg-[#1B3A2F] text-white border-[#1B3A2F]" : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"}`}>
              {c.label} ({count})
            </button>
          );
        })}
        <div className="flex items-center gap-1 ml-auto" data-testid="agenda-view-toggle">
          <button onClick={() => setView("lista")} data-testid="agenda-view-lista"
            className={`px-3 py-1.5 rounded-full text-xs font-medium border flex items-center gap-1 transition-colors ${view === "lista" ? "bg-[#D4651A] text-white border-[#D4651A]" : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"}`}>
            <List className="w-3.5 h-3.5" /> Lista
          </button>
          <button onClick={() => setView("calendario")} data-testid="agenda-view-calendario"
            className={`px-3 py-1.5 rounded-full text-xs font-medium border flex items-center gap-1 transition-colors ${view === "calendario" ? "bg-[#D4651A] text-white border-[#D4651A]" : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"}`}>
            <CalendarRange className="w-3.5 h-3.5" /> Calendário
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-stone-400">Carregando agenda...</div>
      ) : view === "calendario" ? (
        <div className="space-y-4">
          <AgendaCalendarView tasks={filtered} selectedDay={selectedDay} onSelectDay={setSelectedDay} />
          {selectedDay && (
            <Section
              title={`Tarefas de ${selectedDay.split("-").reverse().join("/")}`}
              tasks={filtered.filter((t) => brtDateKey(t.remind_at) === selectedDay)}
              emptyText="Nenhuma tarefa nesse dia."
              {...handlers}
            />
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {agendaToday?.prioridades?.length > 0 && (
            <Card className="p-5 rounded-2xl border-[#D4651A]/30 bg-[#D4651A]/5" data-testid="agenda-priorities-card">
              <div className="flex items-center gap-2 mb-3">
                <ListChecks className="w-4 h-4 text-[#D4651A]" />
                <h2 className="text-sm font-bold uppercase tracking-wide text-[#1B3A2F]">Prioridades do dia</h2>
                <span className="text-xs text-stone-500">
                  {agendaToday.counts.atrasadas > 0 ? `${agendaToday.counts.atrasadas} atrasada(s)` : "nada atrasado"}
                </span>
              </div>
              <ol className="space-y-1.5">
                {agendaToday.prioridades.map((p, i) => (
                  <li key={p.id} data-testid={`priority-item-${p.id}`} className="flex items-start gap-2 text-sm">
                    <span className="font-bold text-[#D4651A] w-4">{i + 1}.</span>
                    <span className="flex-1">
                      <span className={p.overdue ? "text-red-700 font-medium" : "text-stone-800"}>{p.title}</span>
                      <span className="text-xs text-stone-500">
                        {" · "}{p.kind === "compromisso" ? fmtBRT(p.remind_at) : "tarefa do dia"}
                        {p.lead_match ? ` · ${p.lead_match}` : ""}
                        {p.overdue ? " · atrasada" : ""}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </Card>
          )}
          {overdue.length > 0 && (
            <Section title="⚠️ Atrasadas" tasks={overdue} emptyText="" {...handlers} />
          )}
          <Section title="📅 Agenda — compromissos com hora marcada"
            tasks={compromissos} emptyText="Nenhum compromisso marcado. Agenda uma visita!" {...handlers} />
          <Section title="✅ Tarefas do dia — sem hora fixa"
            tasks={tarefas} emptyText="Nenhuma tarefa solta. Carteira em dia!" {...handlers} />
          <Section title="Próximas" tasks={upcoming} emptyText="Nenhuma tarefa futura agendada." {...handlers} />
          {doneTasks.length > 0 && (
            <Section title="Concluídas" tasks={doneTasks} emptyText="" {...handlers} />
          )}
          {tasks.length === 0 && (
            <Card className="p-8 text-center text-stone-400" data-testid="agenda-empty">
              Nenhuma tarefa ainda. Cria aqui ou manda no WhatsApp: "me lembra amanhã 10h de ligar pro banco".
            </Card>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="agenda-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1B3A2F]">{editingId ? "Editar tarefa" : "Nova tarefa"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Título *</Label>
              <Input data-testid="task-form-title" value={form.title} placeholder="Ex: Ligar pro banco sobre o financiamento do Pedro"
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Isso é o quê?</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="task-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="compromisso">Compromisso (hora marcada na agenda)</SelectItem>
                  <SelectItem value="tarefa">Tarefa do dia (sem hora fixa)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="task-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Detectar automaticamente</SelectItem>
                  {Object.entries(CATEGORIES).map(([key, c]) => (
                    <SelectItem key={key} value={key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Data *</Label>
                <Input data-testid="task-form-date" type="date" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">{form.kind === "compromisso" ? "Hora (Brasília) *" : "Lembrete às (Brasília)"}</Label>
                <Input data-testid="task-form-time" type="time" value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Lead relacionado (opcional)</Label>
              <Input data-testid="task-form-lead" value={form.lead_name} placeholder="Ex: Pedro Santos"
                onChange={(e) => setForm({ ...form, lead_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Anotações</Label>
              <Textarea data-testid="task-form-notes" rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="task-form-save"
              className="bg-[#1B3A2F] hover:bg-[#0F1F1A] text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingId ? "Salvar" : "Criar tarefa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
